import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface UserProfileWithRole {
  id: string;
  user_id: string;
  display_name: string | null;
  name: string | null;
  email: string | null;
  location: string | null;
  bio: string | null;
  website: string | null;
  created_at: string;
  updated_at: string;
  company_name: string | null;
  avatar_url: string | null;
  phone_enc: string | null;
  is_public: boolean;
  role: string;
}

export const useUserProfileWithRole = () => {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["profile-with-role", user?.id],
    queryFn: async (): Promise<{ profile: UserProfileWithRole | null; role: string } | null> => {
      if (!user?.id) return null;

      // Get profile and role in parallel for better performance
      const [profileResponse, roleResponse] = await Promise.all([
        supabase
          .from("profiles")
          .select("*")
          .eq("user_id", user.id)
          .single(),
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .single()
      ]);

      if (profileResponse.error && profileResponse.error.code !== 'PGRST116') {
        throw profileResponse.error;
      }

      const role = roleResponse.data?.role || 'user';
      const profile = profileResponse.data ? {
        ...profileResponse.data,
        role
      } : null;

      return { profile, role };
    },
    enabled: !!user?.id,
    staleTime: 3 * 60 * 1000, // 3 minutes - shorter since role might be more dynamic
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    profile: data?.profile || null,
    role: data?.role || 'user',
    isLoading,
    error,
    refetch
  };
};